import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import {
  Check as ApproveIcon, Lock as LockIcon, LockOpen as UnlockIcon, Visibility as ViewIcon,
  Edit as EditIcon, Send as SubmitIcon, Block as DeactivateIcon, PlayArrow as ActivateIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { ACTIONS, RESOURCES } from '../../hooks/usePermissions';
import { accountingAPI, ledgerAPI } from '../../services/api';
import { formatAccountingAmount } from '../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import LedgerListPage from './LedgerListPage';
import LedgerFormDialog from './LedgerFormDialog';
import { LedgerPicker, useLedgerOptions, AccountPicker, CurrencyPicker, CustomerPicker, DepartmentPicker, CostCentrePicker, FiscalYearPicker } from './LedgerPickers';

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const list = (res) => { const d = unwrap(res); return Array.isArray(d) ? d : d.items ?? []; };
const PROJECT_STATUSES = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CANCELLED'];
const BILLING_TYPES = ['FIXED_PRICE', 'TIME_AND_MATERIALS', 'COST_PLUS', 'INTERNAL', 'NON_BILLABLE'];
const BUDGET_STATUSES = ['DRAFT', 'SUBMITTED', 'APPROVED', 'LOCKED'];

const ProjectPicker = (props) => {
  const { options, loading } = useLedgerOptions('projects-list', () =>
    ledgerAPI.getProjects({ limit: 100 }).then((res) => list(res).map((p) => ({
      value: p.project_id, label: `${p.project_code} - ${p.project_name}`,
    })))
  );
  return <LedgerPicker label="Project" {...props} options={options} loading={loading} />;
};

const projectManagerLabel = (user) => {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  return `${name || 'Ledger user'} (#${user?.user_id})${user?.is_active === false ? ' - inactive' : ''}`;
};

const ProjectManagerPicker = ({ label = 'Project manager', value, onChange, required, disabled, helper, allowClear = true }) => {
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    let active = true;
    if (!value) {
      setSelected(null);
      setInputValue('');
      setLookupError('');
      return () => { active = false; };
    }

    setLoading(true);
    accountingAPI.getProjectManager(value)
      .then((response) => {
        if (!active) return;
        const user = unwrap(response);
        if (!user?.user_id) throw new Error('The assigned manager was not found in the ledger directory.');
        setSelected(user);
        setInputValue(projectManagerLabel(user));
        setLookupError('');
      })
      .catch((error) => {
        if (!active) return;
        setSelected({ user_id: Number(value), first_name: 'Unresolved', last_name: 'manager', is_active: false });
        setInputValue(`Ledger user #${value} (unavailable)`);
        setLookupError(error?.response?.data?.message || error.message || 'Could not load the current ledger user.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [value]);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await accountingAPI.getProjectManagers({
          page: 1,
          limit: 20,
          ...(search.trim() ? { search: search.trim() } : {}),
        });
        if (active) {
          setOptions(list(response).filter((user) => user?.is_active !== false));
          setLookupError('');
        }
      } catch (error) {
        if (active) {
          setOptions([]);
          setLookupError(error?.response?.data?.message || 'Could not search the ledger user directory.');
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [search]);

  const pickerOptions = selected && !options.some((user) => String(user.user_id) === String(selected.user_id))
    ? [selected, ...options]
    : options;

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={pickerOptions}
      value={selected}
      inputValue={inputValue}
      loading={loading}
      disabled={disabled}
      disableClearable={!allowClear && Boolean(value)}
      filterOptions={(available) => available}
      getOptionLabel={projectManagerLabel}
      getOptionDisabled={(user) => user?.is_active === false}
      isOptionEqualToValue={(user, choice) => String(user.user_id) === String(choice.user_id)}
      loadingText="Searching active ledger users..."
      noOptionsText={lookupError || 'No active ledger users found'}
      onChange={(_, user) => {
        setSelected(user || null);
        setInputValue(user ? projectManagerLabel(user) : '');
        onChange(user ? user.user_id : '');
      }}
      onInputChange={(_, nextValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(nextValue);
          setSearch(nextValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={lookupError || (selected?.is_active === false ? 'The current manager is inactive or unavailable. Choose an active ledger user to replace this assignment.' : helper)}
          placeholder="Search by ledger user name"
        />
      )}
    />
  );
};

const CostCategoryPicker = (props) => {
  const { options, loading } = useLedgerOptions('project-cost-categories', () =>
    ledgerAPI.getCostCategories({ active_only: true }).then((res) => list(res).map((c) => ({
      value: c.category_id, label: `${c.category_code} - ${c.category_name}`,
    })))
  );
  return <LedgerPicker label="Cost category" {...props} options={options} loading={loading} />;
};

const ProjectStatus = ({ status, active }) => (
  <Box display="flex" gap={0.5} flexWrap="wrap">
    <Chip size="small" variant="outlined" label={status || 'PLANNING'} color={status === 'ACTIVE' ? 'success' : 'default'} />
    <Chip size="small" label={active === false ? 'Inactive' : 'Active'} color={active === false ? 'default' : 'success'} />
  </Box>
);

const ProjectFields = ({ editing = false }) => [
  ...(!editing ? [
    { key: 'project_code', label: 'Project code', required: true, md: 4 },
    { key: 'currency_id', label: 'Currency', type: 'picker', picker: CurrencyPicker, required: true, md: 4 },
    { key: 'billing_type', label: 'Billing type', type: 'select', options: BILLING_TYPES.map((v) => ({ label: v.replaceAll('_', ' '), value: v })), md: 4 },
  ] : []),
  { key: 'project_name', label: 'Project name', required: true, md: 6 },
  { key: 'description', label: 'Description', type: 'textarea', md: 6 },
  ...(!editing ? [] : [
    { key: 'status', label: 'Project status', type: 'select', options: PROJECT_STATUSES.map((v) => ({ label: v.replaceAll('_', ' '), value: v })), md: 6 },
    { key: 'billing_type', label: 'Billing type', type: 'select', options: BILLING_TYPES.map((v) => ({ label: v.replaceAll('_', ' '), value: v })), md: 6 },
  ]),
  { key: 'parent_project_id', label: 'Parent project (optional)', type: 'picker', picker: ProjectPicker, md: 6 },
  { key: 'customer_id', label: 'Customer / sponsor (optional)', type: 'picker', picker: CustomerPicker, md: 6 },
  { key: 'project_manager_user_id', label: 'Project manager (optional)', type: 'picker', picker: ProjectManagerPicker, pickerProps: { allowClear: !editing }, md: 6, helper: 'Search the active users synced to this ledger. The ledger validates the assignment when you save.' },
  { key: 'department_id', label: 'Department (optional)', type: 'picker', picker: DepartmentPicker, md: 6 },
  { key: 'cost_centre_id', label: 'Cost centre (optional)', type: 'picker', picker: CostCentrePicker, md: 6 },
  { key: 'revenue_account_id', label: 'Revenue account (optional)', type: 'picker', picker: AccountPicker, md: 6 },
  { key: 'contract_value', label: 'Contract / grant amount (optional)', type: 'number', min: 0, md: 6 },
  { key: 'start_date', label: 'Start date', type: 'date', md: 6 },
  { key: 'end_date', label: 'End date', type: 'date', md: 6 },
];

const ProjectBudgetLineFields = ({ editing = false, values }) => [
  ...(!editing ? [
    { key: 'project_id', label: 'Project', type: 'picker', picker: ProjectPicker, required: true, md: 6 },
    { key: 'line_type', label: 'Line type', type: 'select', options: [{ label: 'Cost', value: 'COST' }, { label: 'Revenue', value: 'REVENUE' }], md: 6 },
    ...(values.line_type === 'COST' ? [{ key: 'category_id', label: 'Cost category', type: 'picker', picker: CostCategoryPicker, required: true, md: 6 }] : []),
    { key: 'fiscal_year_id', label: 'Fiscal year (optional)', type: 'picker', picker: FiscalYearPicker, md: 6 },
  ] : []),
  { key: 'budgeted_amount', label: 'Budgeted amount', type: 'number', min: 0, required: true, md: 6 },
  { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
];

const ProjectsList = () => {
  const { can } = useAccessControl();
  const resource = RESOURCES.LEDGER.BUDGETING;
  const canCreate = can(resource, ACTIONS.CREATE);
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [refreshKey, setRefreshKey] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const reload = () => setRefreshKey((k) => k + 1);
  const startCreate = () => { setEditing(null); setValues({ billing_type: 'NON_BILLABLE', status: 'PLANNING' }); setFormOpen(true); };
  const startEdit = (row) => {
    setEditing(row);
    setValues({
      project_name: row.project_name || '', description: row.description || '', status: row.status || 'PLANNING',
      billing_type: row.billing_type || 'NON_BILLABLE', parent_project_id: row.parent_project_id || '',
      customer_id: row.customer_id || '', project_manager_user_id: row.project_manager_user_id || '', department_id: row.department_id || '', cost_centre_id: row.cost_centre_id || '',
      revenue_account_id: row.revenue_account_id || '', contract_value: row.contract_value ?? '',
      start_date: row.start_date ? String(row.start_date).slice(0, 10) : '', end_date: row.end_date ? String(row.end_date).slice(0, 10) : '',
    });
    setFormOpen(true);
  };

  const save = async () => {
    if (!String(values.project_name || '').trim()) return showSnackbar('Project name is required.', 'error');
    if (!editing && (!String(values.project_code || '').trim() || !values.currency_id)) return showSnackbar('Project code and currency are required.', 'error');
    const payload = { ...values };
    ['currency_id', 'parent_project_id', 'customer_id', 'project_manager_user_id', 'department_id', 'cost_centre_id', 'revenue_account_id'].forEach((key) => {
      if (payload[key]) payload[key] = Number(payload[key]); else delete payload[key];
    });
    if (editing && String(values.project_manager_user_id || '') === String(editing.project_manager_user_id || '')) {
      // Leave an unchanged manager out of the update payload. This lets
      // unrelated edits proceed if the historic assignment is now inactive.
      delete payload.project_manager_user_id;
    }
    if (payload.contract_value !== '' && payload.contract_value != null) payload.contract_value = Number(payload.contract_value); else delete payload.contract_value;
    ['start_date', 'end_date'].forEach((key) => { if (!payload[key]) delete payload[key]; });
    setSaving(true);
    try {
      if (editing) await ledgerAPI.updateProject(editing.project_id, payload);
      else await ledgerAPI.createProject({ ...payload, project_code: values.project_code.trim(), project_name: values.project_name.trim() });
      showSnackbar(editing ? 'Project updated.' : 'Project created.', 'success');
      setFormOpen(false); reload();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Could not save project.', 'error');
    } finally { setSaving(false); }
  };

  const openReports = async (project) => {
    try {
      const [base, costs, wip, profitability, budget] = await Promise.all([
        ledgerAPI.getProject(project.project_id), ledgerAPI.getProjectCosts(project.project_id),
        ledgerAPI.getProjectWip(project.project_id), ledgerAPI.getProjectProfitability(project.project_id),
        ledgerAPI.getProjectBudgetVsActual(project.project_id),
      ]);
      setDetail({ project: unwrap(base), costs: unwrap(costs), wip: unwrap(wip), profitability: unwrap(profitability), budget: unwrap(budget) });
    } catch (error) { showSnackbar(error?.response?.data?.message || 'Could not load project reports.', 'error'); }
  };

  const stateAction = (row, action, label) => confirm({
    title: `${label} project`, message: `${label} ${row.project_code} — ${row.project_name}?`,
    onConfirm: async () => {
      try { await action(row.project_id); showSnackbar(`Project ${label.toLowerCase()}d.`, 'success'); reload(); }
      catch (error) { showSnackbar(error?.response?.data?.message || `Could not ${label.toLowerCase()} project.`, 'error'); }
    },
  });

  return <>
    <LedgerListPage
      resource={resource} title="Projects & Grants" subtitle="Track project ownership, budgets, posted costs, work in progress and profitability."
      addLabel="New Project" idKey="project_id" searchEnabled={false} refreshKey={refreshKey}
      fetchItems={(params) => ledgerAPI.getProjects(params)} onAdd={canCreate ? startCreate : null}
      filters={[
        { key: 'status', label: 'Status', options: PROJECT_STATUSES.map((v) => ({ label: v.replaceAll('_', ' '), value: v })) },
        { key: 'is_active', label: 'Availability', options: [{ label: 'Active records', value: 'true' }] },
      ]}
      columns={[
        { label: 'Code', render: (p) => <strong>{p.project_code}</strong> },
        { label: 'Project / parent', render: (p) => <>{p.project_name}<br /><Typography variant="caption" color="text.secondary">{p.parent?.project_code ? `Under ${p.parent.project_code}` : p.billing_type?.replaceAll('_', ' ')}</Typography></> },
        { label: 'Sponsor / customer', render: (p) => p.customer?.name || '—' },
        { label: 'Project manager', render: (p) => p.manager ? `${p.manager.first_name} ${p.manager.last_name}`.trim() : '—' },
        { label: 'Contract / grant', render: (p) => formatAccountingAmount(p.contract_value), align: 'right' },
        { label: 'Status', render: (p) => <ProjectStatus status={p.status} active={p.is_active} /> },
      ]}
      rowActions={(p) => {
        const actions = [
          { icon: ViewIcon, title: 'Project reports', onClick: () => openReports(p) },
          ...(canUpdate ? [{ icon: EditIcon, title: 'Edit project', onClick: () => startEdit(p) }] : []),
        ];
        if (canCreate && p.is_active === false) actions.push({ icon: ActivateIcon, title: 'Activate project', onClick: () => stateAction(p, ledgerAPI.activateProject, 'Activate') });
        if (canCreate && p.is_active !== false) actions.push({ icon: DeactivateIcon, title: 'Deactivate project', color: 'error', onClick: () => stateAction(p, ledgerAPI.deactivateProject, 'Deactivate') });
        return actions;
      }}
    />
    <LedgerFormDialog open={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Edit ${editing.project_code}` : 'New Project'}
      fields={ProjectFields({ editing })} values={values} onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))} onSubmit={save} saving={saving} />
    <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
      <DialogTitle>{detail?.project?.project_code} · {detail?.project?.project_name}</DialogTitle>
      <DialogContent>
        {detail && <>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Chip label={`Posted cost ${formatAccountingAmount(detail.profitability.total_cost)}`} />
            <Chip label={`Revenue ${formatAccountingAmount(detail.profitability.total_revenue)}`} color="primary" />
            <Chip label={`Margin ${formatAccountingAmount(detail.profitability.margin)}`} color={detail.profitability.margin < 0 ? 'error' : 'success'} />
            <Chip label={`WIP ${formatAccountingAmount(detail.wip.wip_balance)}`} color="warning" />
          </Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>Budget versus actual</Typography>
          {detail.budget.rows?.length ? <Table size="small"><TableHead><TableRow><TableCell>Type</TableCell><TableCell>Category</TableCell><TableCell>Budget</TableCell><TableCell>Actual</TableCell><TableCell>Variance</TableCell></TableRow></TableHead><TableBody>
            {detail.budget.rows.map((row) => <TableRow key={row.budget_line_id}><TableCell>{row.line_type}</TableCell><TableCell>{row.category_name || 'Project total'}</TableCell><TableCell>{formatAccountingAmount(row.budgeted_amount)}</TableCell><TableCell>{formatAccountingAmount(row.actual_amount)}</TableCell><TableCell>{formatAccountingAmount(row.variance)}</TableCell></TableRow>)}
          </TableBody></Table> : <Typography variant="body2" color="text.secondary">No project budget lines yet.</Typography>}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>Posted costs by category</Typography>
          {detail.costs.categories?.length ? <Table size="small"><TableHead><TableRow><TableCell>Category</TableCell><TableCell align="right">Posted cost</TableCell></TableRow></TableHead><TableBody>
            {detail.costs.categories.map((row) => <TableRow key={row.category_id}><TableCell>{row.category_code} · {row.category_name}</TableCell><TableCell align="right">{formatAccountingAmount(row.amount)}</TableCell></TableRow>)}
          </TableBody></Table> : <Typography variant="body2" color="text.secondary">No posted costs linked to this project.</Typography>}
        </>}
      </DialogContent>
      <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
    </Dialog>
    {ConfirmDialogComponent}{SnackbarComponent}
  </>;
};

const ProjectBudgets = () => {
  const { can } = useAccessControl();
  const resource = RESOURCES.LEDGER.BUDGETING;
  const canCreate = can(resource, ACTIONS.CREATE); const canUpdate = can(resource, ACTIONS.UPDATE);
  const canApprove = can(resource, ACTIONS.APPROVE); const canDelete = can(resource, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [refreshKey, setRefreshKey] = useState(0); const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); const [values, setValues] = useState({ line_type: 'COST' }); const [saving, setSaving] = useState(false);
  const reload = () => setRefreshKey((k) => k + 1);
  const openCreate = () => { setEditing(null); setValues({ line_type: 'COST', budgeted_amount: '', notes: '' }); setFormOpen(true); };
  const openEdit = (row) => { setEditing(row); setValues({ budgeted_amount: row.budgeted_amount ?? '', notes: row.notes || '' }); setFormOpen(true); };
  const action = (row, title, fn, success) => confirm({ title, message: `${title} for ${row.project?.project_code} / ${row.category?.category_name || row.line_type}?`, onConfirm: async () => {
    try { await fn(row.budget_line_id); showSnackbar(success, 'success'); reload(); }
    catch (error) { showSnackbar(error?.response?.data?.message || `${title} failed.`, 'error'); }
  } });
  const save = async () => {
    if (!(Number(values.budgeted_amount) >= 0)) return showSnackbar('Enter a valid budget amount.', 'error');
    if (!editing && !values.project_id) return showSnackbar('Choose a project.', 'error');
    if (!editing && values.line_type === 'COST' && !values.category_id) return showSnackbar('Choose a cost category for a cost budget.', 'error');
    const payload = { budgeted_amount: Number(values.budgeted_amount), notes: values.notes || undefined };
    setSaving(true);
    try {
      if (editing) await ledgerAPI.updateProjectBudgetLine(editing.budget_line_id, payload);
      else {
        payload.project_id = Number(values.project_id); payload.line_type = values.line_type;
        if (values.category_id) payload.category_id = Number(values.category_id);
        if (values.fiscal_year_id) payload.fiscal_year_id = Number(values.fiscal_year_id);
        await ledgerAPI.createProjectBudgetLine(payload);
      }
      showSnackbar(editing ? 'Budget line updated.' : 'Budget line created as draft.', 'success'); setFormOpen(false); reload();
    } catch (error) { showSnackbar(error?.response?.data?.message || 'Could not save budget line.', 'error'); }
    finally { setSaving(false); }
  };
  return <>
    <LedgerListPage resource={resource} title="Project Budget Lines" subtitle="Budget cost and revenue by project, category and fiscal year; submit, approve and lock the plan."
      addLabel="New Budget Line" idKey="budget_line_id" searchEnabled={false} refreshKey={refreshKey} fetchItems={(params) => ledgerAPI.getProjectBudgetLines(params)} onAdd={canCreate ? openCreate : null}
      filters={[
        { type: 'picker', key: 'project_id', label: 'Project', picker: ProjectPicker, width: 260 },
        { key: 'status', label: 'Status', options: BUDGET_STATUSES.map((v) => ({ label: v, value: v })) },
      ]}
      columns={[
        { label: 'Project', render: (row) => <>{row.project?.project_code || `#${row.project_id}`}<br /><Typography variant="caption">{row.project?.project_name}</Typography></> },
        { label: 'Type / category', render: (row) => `${row.line_type}${row.category?.category_name ? ` · ${row.category.category_name}` : ''}` },
        { label: 'Fiscal year', render: (row) => row.fiscal_year?.year_name || row.fiscal_year_id || 'All years' },
        { label: 'Budget', render: (row) => formatAccountingAmount(row.budgeted_amount), align: 'right' },
        { label: 'Status', render: (row) => <Chip size="small" label={row.status} color={row.status === 'APPROVED' || row.status === 'LOCKED' ? 'success' : 'default'} variant="outlined" /> },
      ]}
      rowActions={(row) => {
        const actions = [];
        if (row.status === 'DRAFT' && canUpdate) actions.push({ icon: EditIcon, title: 'Edit draft', onClick: () => openEdit(row) });
        if (row.status === 'DRAFT' && canCreate) actions.push({ icon: SubmitIcon, title: 'Submit for approval', onClick: () => action(row, 'Submit budget line', ledgerAPI.submitProjectBudgetLine, 'Budget line submitted.') });
        if (['DRAFT', 'SUBMITTED'].includes(row.status) && canApprove) actions.push({ icon: ApproveIcon, title: 'Approve', onClick: () => action(row, 'Approve budget line', ledgerAPI.approveProjectBudgetLine, 'Budget line approved.') });
        if (row.status === 'SUBMITTED' && canApprove) actions.push({ icon: DeactivateIcon, title: 'Reject to draft', color: 'error', onClick: () => action(row, 'Reject budget line', ledgerAPI.rejectProjectBudgetLine, 'Budget line returned to draft.') });
        if (row.status === 'APPROVED' && canApprove) actions.push({ icon: LockIcon, title: 'Lock budget line', onClick: () => action(row, 'Lock budget line', ledgerAPI.lockProjectBudgetLine, 'Budget line locked.') });
        if (row.status === 'LOCKED' && canApprove) actions.push({ icon: UnlockIcon, title: 'Unlock budget line', onClick: () => action(row, 'Unlock budget line', ledgerAPI.unlockProjectBudgetLine, 'Budget line unlocked.') });
        if (row.status === 'DRAFT' && canDelete) actions.push({ icon: DeleteIcon, title: 'Delete draft', color: 'error', onClick: () => action(row, 'Delete budget line', ledgerAPI.deleteProjectBudgetLine, 'Budget line deleted.') });
        return actions;
      }}
    />
    <LedgerFormDialog open={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Edit Project Budget Line' : 'New Project Budget Line'}
      fields={ProjectBudgetLineFields({ editing, values })} values={values} onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))} onSubmit={save} saving={saving} maxWidth="md" />
    {ConfirmDialogComponent}{SnackbarComponent}
  </>;
};

const Projects = () => {
  const [tab, setTab] = useState('projects');
  const { can } = useAccessControl();
  if (!can(RESOURCES.LEDGER.BUDGETING, ACTIONS.READ)) return <Alert severity="error">You do not have permission to view Projects.</Alert>;
  return <Box>
    <ModulePageHeader title="Projects & Grants" subtitle="Manage project records, budget approvals, grant amounts, and project performance." />
    <Card sx={{ mb: 3 }}><CardContent sx={{ pb: '8px !important', pt: '4px !important' }}>
      <Tabs value={tab} onChange={(_, v) => v && setTab(v)}><Tab value="projects" label="Projects" /><Tab value="budgets" label="Budget Lines" /></Tabs>
    </CardContent></Card>
    {tab === 'projects' ? <ProjectsList /> : <ProjectBudgets />}
  </Box>;
};

export default Projects;
